import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Sparkles, Loader2, Download, Printer, Copy, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { api } from '../lib/api.jsx';
import { parseHash } from '../lib/router.jsx';

// Même borne que le serveur (AiReportsController.MaxPromptLength).
const PROMPT_MAX = 4000;

const EXAMPLES = [
  "Serveurs Windows avec moins de 15 % d'espace disque libre, par client",
  'Postes de la Clinique M sans redémarrage depuis 30 jours',
  "Disques SSD avec usure supérieure à 70 %",
  'Historique des alertes critiques du dernier mois par client',
];

const STATUS = {
  queued: { label: 'En file', cls: 'bg-slate-500/15 border-slate-500/30 text-muted' },
  running: { label: 'En cours', cls: 'bg-blue-500/15 border-blue-500/30 text-blue-400' },
  done: { label: 'Terminé', cls: 'bg-ok/15 border-ok/30 text-ok' },
  error: { label: 'Erreur', cls: 'bg-crit/15 border-crit/30 text-crit' },
};

const btnCls =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rule bg-card text-fg-2 text-xs cursor-pointer hover:bg-card-hi disabled:opacity-50 disabled:cursor-not-allowed';

const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');
const fmtCost = (c) => (c == null ? '—' : Number(c).toFixed(2).replace('.', ',') + ' $');
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('fr-FR'));
const isPending = (s) => s === 'queued' || s === 'running';

// Hook DOMPurify posé une seule fois : tout lien du rapport s'ouvre dans un
// nouvel onglet sans accès à window.opener (le contenu vient d'un LLM).
let hookInstalled = false;
function renderMarkdown(md) {
  if (!hookInstalled) {
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
    hookInstalled = true;
  }
  const html = DOMPurify.sanitize(marked.parse(md || '', { async: false }));
  // Enveloppe chaque tableau pour qu'il défile horizontalement tout seul ;
  // fait APRÈS l'assainissement, sur le DOM d'un gabarit inerte.
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  tpl.content.querySelectorAll('table').forEach((t) => {
    const w = document.createElement('div');
    w.className = 'ai-table-wrap';
    t.replaceWith(w);
    w.appendChild(t);
  });
  return tpl.innerHTML;
}

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.queued;
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${s.cls}`}>
      {s.label}
    </span>
  );
};

export function AiReportsPage() {
  const [config, setConfig] = useState(null);
  const [prompt, setPrompt] = useState('');
  const promptRef = useRef(null);
  // La zone suit son contenu : une demande détaillée (colonnes voulues, filtres,
  // tri) se relit d'un bloc au lieu de défiler dans trois lignes. Plafonnée à
  // 60 % de l'écran pour que le bouton « Générer » reste visible.
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight + 2, Math.round(window.innerHeight * 0.6)) + 'px';
  }, [prompt]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [history, setHistory] = useState([]);
  const [selectedId, setSelectedId] = useState(() => parseHash().query?.r || null);
  const [report, setReport] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [notice, setNotice] = useState('');
  const pollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    const h = await api.get('/platform/ai/reports');
    if (Array.isArray(h)) setHistory(h);
  }, []);

  useEffect(() => {
    api.get('/platform/ai/config').then((c) => setConfig(c || { enabled: false }));
    loadHistory();
  }, [loadHistory]);

  // L'id vit dans l'URL (replaceState : pas d'événement hashchange, donc pas de
  // remontage de la vue) pour que F5 ou un lien partagé rouvre le même rapport.
  const select = (id) => {
    setSelectedId(id);
    setReport(null);
    const h = id ? '#/ai?r=' + encodeURIComponent(id) : '#/ai';
    if (window.location.hash !== h) history_replace(h);
  };

  // Chargement + sondage toutes les 2 s tant que le rapport n'est pas terminé.
  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    const tick = async () => {
      const r = await api.get('/platform/ai/reports/' + selectedId);
      if (cancelled) return;
      if (!r) {
        setReport({ id: selectedId, status: 'error', error: 'Rapport introuvable.' });
        return;
      }
      setReport(r);
      if (isPending(r.status)) {
        pollRef.current = setTimeout(tick, 2000);
      } else {
        loadHistory();
      }
    };
    tick();
    return () => {
      cancelled = true;
      clearTimeout(pollRef.current);
    };
  }, [selectedId, loadHistory]);

  // Chronomètre affiché pendant l'attente
  useEffect(() => {
    if (!report || !isPending(report.status)) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [report?.status]);

  const submit = async (text) => {
    const p = (text ?? prompt).trim();
    if (!p || submitting || !config?.enabled) return;
    setSubmitting(true);
    setFormError('');
    try {
      const res = await api.fetch('/platform/ai/reports', { method: 'POST', body: JSON.stringify({ prompt: p }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFormError(body.error || 'Échec de la demande.');
      } else {
        select(body.id);
        loadHistory();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!confirm('Supprimer ce rapport ?')) return;
    const res = await api.fetch('/platform/ai/reports/' + id, { method: 'DELETE' });
    if (res.status === 409) {
      setNotice('Ce rapport est encore en cours : suppression impossible.');
      return;
    }
    if (selectedId === id) select(null);
    loadHistory();
  };

  const exportExcel = async () => {
    setNotice('');
    const res = await api.fetch('/platform/ai/reports/' + report.id + '/excel');
    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      setNotice(b.error || "Échec de l'export Excel.");
      return;
    }
    const blob = await res.blob();
    const dl = document.createElement('a');
    dl.href = URL.createObjectURL(blob);
    dl.download = `rapport-ia-${report.id}.xlsx`;
    document.body.appendChild(dl);
    dl.click();
    dl.remove();
    URL.revokeObjectURL(dl.href);
  };

  const printReport = () => {
    // La classe sur body active le CSS d'impression ; retirée après coup.
    document.body.classList.add('ai-printing');
    const done = () => {
      document.body.classList.remove('ai-printing');
      window.removeEventListener('afterprint', done);
    };
    window.addEventListener('afterprint', done);
    window.print();
  };

  const copyMd = async () => {
    try {
      await navigator.clipboard.writeText(report.resultMarkdown || '');
      setNotice('Markdown copié.');
    } catch {
      setNotice('Copie impossible dans ce navigateur.');
    }
  };

  const html = useMemo(
    () => (report?.status === 'done' ? renderMarkdown(report.resultMarkdown) : ''),
    [report?.status, report?.resultMarkdown]
  );

  const enabled = !!config?.enabled;
  const elapsed = report?.createdAt ? Math.max(0, Math.round((now - new Date(report.createdAt).getTime()) / 1000)) : 0;

  return (
    <div>
      <h1 className="text-[22px] font-bold text-fg flex items-center gap-2 mb-5 no-print">
        <Sparkles size={20} strokeWidth={1.75} className="text-blue-400" /> IA
      </h1>

      {config && !enabled && (
        <div className="mb-4 p-3 rounded-lg border border-warn/30 bg-warn/10 text-warn text-xs no-print">
          L'IA n'est pas encore configurée sur cette plateforme (clé API Anthropic manquante).
        </div>
      )}

      <div className="bg-card border border-rule rounded-xl p-4 mb-5 no-print">
        <textarea
          ref={promptRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              submit();
            }
          }}
          disabled={!enabled}
          rows={8}
          maxLength={PROMPT_MAX}
          placeholder={
            'Décrivez le rapport voulu, par exemple :\n' + EXAMPLES.slice(0, 3).map((e) => '• ' + e).join('\n')
          }
          className="w-full min-h-[180px] bg-sidebar border border-rule rounded-lg px-3 py-2 text-fg-2 text-sm leading-relaxed outline-none focus:border-blue-500 resize-y disabled:opacity-50"
        />
        {prompt.length > PROMPT_MAX * 0.8 && (
          <div className={`text-right text-[11px] mt-1 ${prompt.length >= PROMPT_MAX ? 'text-crit' : 'text-muted-2'}`}>
            {prompt.length} / {PROMPT_MAX}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 mt-2">
          {EXAMPLES.map((e) => (
            <button
              key={e}
              type="button"
              disabled={!enabled}
              onClick={() => setPrompt(e)}
              className="px-2.5 py-1 rounded-full border border-rule bg-sidebar text-muted text-[11px] cursor-pointer hover:text-fg-2 disabled:opacity-50"
            >
              {e}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2 mt-3">
          <div className="text-[11px] text-muted-2">
            {config?.model ? `Modèle ${config.model} · ` : ''}
            {config?.maxCostUsd != null ? `plafond ${String(config.maxCostUsd).replace('.', ',')} $ par rapport` : ''}
          </div>
          <div className="flex items-center gap-3">
            {formError && <span className="text-crit text-xs">{formError}</span>}
            <button
              onClick={() => submit()}
              disabled={!enabled || submitting || !prompt.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-500 text-white text-xs font-semibold cursor-pointer border-none hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Générer le rapport
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[320px_1fr] items-start">
        {/* Historique */}
        <div className="bg-card border border-rule rounded-xl overflow-hidden no-print">
          <div className="px-4 py-2.5 border-b border-rule text-xs font-bold text-fg">Historique</div>
          {history.length === 0 ? (
            <div className="p-4 text-xs text-muted-2">Aucun rapport pour l'instant.</div>
          ) : (
            <ul className="max-h-[70vh] overflow-auto">
              {history.map((h) => (
                <li
                  key={h.id}
                  onClick={() => select(h.id)}
                  className={`px-4 py-2.5 border-b border-rule cursor-pointer hover:bg-card-hi ${
                    selectedId === h.id ? 'bg-card-hi' : ''
                  }`}
                >
                  <div className="text-xs text-fg-2 line-clamp-2" title={h.prompt}>
                    {h.prompt}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <StatusBadge status={h.status} />
                    <span className="text-[10px] text-muted-2">{fmtDate(h.createdAt)}</span>
                    <span className="text-[10px] text-muted-2">{fmtCost(h.costUsd)}</span>
                    <span className="ml-auto flex gap-1">
                      <button
                        title="Relancer (les données sont recalculées)"
                        disabled={!enabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          submit(h.prompt);
                        }}
                        className="border-none bg-transparent text-muted-2 cursor-pointer p-1 hover:text-fg-2 disabled:opacity-40"
                      >
                        <RefreshCw size={13} />
                      </button>
                      <button
                        title="Supprimer"
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(h.id);
                        }}
                        className="border-none bg-transparent text-muted-2 cursor-pointer p-1 hover:text-crit"
                      >
                        <Trash2 size={13} />
                      </button>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Rapport */}
        <div className="min-w-0">
          {!selectedId && (
            <div className="bg-card border border-rule rounded-xl p-8 text-center text-xs text-muted-2 no-print">
              Générez un rapport ou ouvrez-en un depuis l'historique.
            </div>
          )}
          {selectedId && !report && (
            <div className="p-8 text-center text-muted-2 text-xs no-print">Chargement…</div>
          )}
          {report && isPending(report.status) && (
            <div className="bg-card border border-rule rounded-xl p-6 flex items-start gap-3">
              <Loader2 size={20} className="animate-spin text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm text-fg font-semibold">
                  {report.status === 'queued' ? 'En file d’attente…' : 'Analyse en cours…'}
                  <span className="ml-2 text-xs text-muted-2 font-normal">
                    {Math.floor(elapsed / 60)} min {String(elapsed % 60).padStart(2, '0')} s
                  </span>
                </div>
                <div className="text-xs text-muted mt-1 break-words">{report.progress || ''}</div>
              </div>
            </div>
          )}
          {report && report.status === 'error' && (
            <div className="bg-card border border-crit/30 rounded-xl p-5">
              <div className="flex items-center gap-2 text-crit text-sm font-semibold">
                <AlertTriangle size={16} /> Le rapport a échoué
              </div>
              <div className="text-xs text-fg-2 mt-2 break-words">{report.error || 'Erreur inconnue.'}</div>
              {report.prompt && (
                <button className={`${btnCls} mt-3`} disabled={!enabled} onClick={() => submit(report.prompt)}>
                  <RefreshCw size={13} /> Relancer
                </button>
              )}
            </div>
          )}
          {report && report.status === 'done' && (
            <div className="ai-print-area bg-card border border-rule rounded-xl">
              <div className="flex items-center gap-2 flex-wrap px-4 py-3 border-b border-rule no-print">
                <button className={btnCls} onClick={exportExcel}>
                  <Download size={13} /> Exporter Excel
                </button>
                <button className={btnCls} onClick={printReport}>
                  <Printer size={13} /> Imprimer / PDF
                </button>
                <button className={btnCls} onClick={copyMd}>
                  <Copy size={13} /> Copier le Markdown
                </button>
                <button className={btnCls} disabled={!enabled} onClick={() => submit(report.prompt)}>
                  <RefreshCw size={13} /> Relancer
                </button>
                {notice && <span className="text-xs text-muted">{notice}</span>}
              </div>
              <div className="px-4 pt-3 text-[11px] text-muted-2 italic">{report.prompt}</div>
              <div className="p-4">
                <div className="ai-md" dangerouslySetInnerHTML={{ __html: html }} />
              </div>
              <div className="px-4 py-3 border-t border-rule text-[11px] text-muted-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span>Modèle : {report.model || '—'}</span>
                <span>Appels d'outils : {fmtNum(report.toolCalls)}</span>
                <span>
                  Jetons : {fmtNum(report.inputTokens)} entrée / {fmtNum(report.outputTokens)} sortie
                  {report.cacheReadTokens ? ` / ${fmtNum(report.cacheReadTokens)} cache` : ''}
                </span>
                <span>Coût : {fmtCost(report.costUsd)}</span>
                <span>Généré le {fmtDate(report.completedAt)}</span>
                {report.budgetReached && (
                  <span className="px-2 py-0.5 rounded border border-warn/30 bg-warn/10 text-warn font-semibold">
                    plafond atteint, rapport possiblement incomplet
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Met à jour le hash sans déclencher « hashchange » : Dashboard ne doit pas
// réagir à un simple changement de rapport sélectionné.
function history_replace(hash) {
  window.history.replaceState(null, '', hash);
}
